/**
 * Wording and calendar helpers for the order guide. Pure, so the grid, the
 * board and the order text all say the same thing.
 *
 * Store days arrive as `YYYY-MM-DD` strings: calendar days on the store's
 * clock, not instants. They are read and formatted as UTC so the browser's own
 * zone never moves a day (a `new Date('2026-10-05')` shown in New York is
 * Oct 4).
 */

import type {
  GuideLine,
  RhythmOutcome,
  RhythmResolution,
  StockTier,
  StoreWeek,
  VendorProfile,
  VendorRhythm,
} from './types';
import {
  nextOrderChain,
  type ChainDestination,
  type NextOrderChain,
} from '@kk-pos/shared/drop-off';

const DAY_MS = 86_400_000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
/** The API's default lead time when a vendor link has none. */
export const DEFAULT_LEAD_DAYS = 2;

const monthDay = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
/** For instants (a count's timestamp): the browser's zone is the operator's. */
const localMonthDay = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

export function dayNumber(isoDay: string): number {
  return Math.round(Date.parse(`${isoDay}T00:00:00Z`) / DAY_MS);
}

export function addDays(isoDay: string, days: number): string {
  return new Date((dayNumber(isoDay) + days) * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(fromDay: string, toDay: string): number {
  return dayNumber(toDay) - dayNumber(fromDay);
}

export function weekdayOf(isoDay: string): number {
  return new Date(dayNumber(isoDay) * DAY_MS).getUTCDay();
}

export function weekdayName(dow: number): string {
  return WEEKDAYS[((dow % 7) + 7) % 7]!;
}

/** `Oct 5`. */
export function dayLabel(isoDay: string | null | undefined, fallback = '—'): string {
  if (!isoDay || !/^\d{4}-\d{2}-\d{2}/.test(isoDay)) return fallback;
  return monthDay.format(new Date(dayNumber(isoDay.slice(0, 10)) * DAY_MS));
}

/** `Mon Oct 5`. */
export function weekdayDayLabel(isoDay: string): string {
  return `${weekdayName(weekdayOf(isoDay))} ${dayLabel(isoDay)}`;
}

/** `Sep 21 – 27`, or `Sep 28 – Oct 4` across a month. */
export function weekRangeLabel(week: Pick<StoreWeek, 'from' | 'to'>): string {
  const from = dayLabel(week.from);
  const to = dayLabel(week.to);
  const [fromMonth] = from.split(' ');
  const [toMonth, toDay] = to.split(' ');
  return fromMonth === toMonth ? `${from} – ${toDay}` : `${from} – ${toMonth} ${toDay}`;
}

/**
 * The weekdays a vendor normally delivers on: those with at least 2 arrivals in
 * 16 weeks and at least a quarter of the busiest weekday's count, so a one-off
 * extra drop does not read as a delivery day.
 */
export function deliveryWeekdays(rhythm: Pick<VendorRhythm, 'usualWeekdays'>): number[] {
  const top = rhythm.usualWeekdays[0]?.n ?? 0;
  return rhythm.usualWeekdays
    .filter((d) => d.n >= 2 && d.n >= top / 4)
    .map((d) => d.dow)
    .sort((a, b) => a - b);
}

export interface NextDelivery {
  /** The day expected AT THE STORE: the next delivery an order placed today can still make. */
  deliveryOn: string;
  /** The day the order has to go out for it. */
  orderOn: string;
  /** Days from asOf to orderOn: 0 = today, 1 = tomorrow. */
  orderInDays: number;
  /**
   * TASK-0256 (DROP-1): the day the vendor drops it at its drop-off location; null when direct, or when only
   * the store days (from past deliveries) are known.
   */
  dropOn?: string | null;
  /** The drop-off location it goes through; null = direct to the store. */
  via?: { locationId: number; name: string } | null;
}

/** TASK-0256: the vendor's drop-off location as the date chain reads it, from its How we order. */
export function destinationOf(
  profile: Partial<VendorProfile> | null | undefined
): ChainDestination | null {
  const to = profile?.shipTo;
  if (!to) return null;
  return {
    locationId: to.locationId,
    name: to.name,
    transferDays: to.transferDays,
    transferWeekdays: to.transferWeekdays,
  };
}

/**
 * The next delivery an order placed on or after `asOf` can make, given the
 * vendor's usual delivery weekdays (days deliveries REACH THE STORE, from receipts)
 * and a lead time, skipping any quiet period ("no delivery expected until").
 * Null when the vendor has no usual delivery day yet. The arithmetic is the
 * shared next-order chain (@kk-pos/shared/drop-off), so the API and the console agree.
 */
export function nextDelivery(
  asOf: string,
  rhythm: Pick<VendorRhythm, 'usualWeekdays'> & { quietUntil?: string | null },
  leadDays = DEFAULT_LEAD_DAYS
): NextDelivery | null {
  return asNext(
    nextOrderChain(
      asOf,
      { leadTimeDays: Math.round(leadDays) },
      deliveryWeekdays(rhythm),
      {
        quietUntil: rhythm.quietUntil ?? null,
      }
    )
  );
}

/** The chain as NextDelivery: a direct vendor's answer has exactly TASK-0249's three fields. */
function asNext(chain: NextOrderChain | null): NextDelivery | null {
  if (!chain) return null;
  const { dropOn, via, ...days } = chain;
  return via ? { ...days, dropOn, via } : days;
}

/**
 * TASK-0249 (INC-1c): minutes since midnight on the store's clock (its zone from the API), so "order today
 * by 2 pm" turns into tomorrow at 2 pm store time wherever the browser is. Null when the zone is unknown
 * to this browser.
 */
export function storeMinutesNow(
  timeZone: string | undefined,
  now: Date = new Date()
): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    const minute = Number(parts.find((p) => p.type === 'minute')?.value);
    return Number.isFinite(hour) && Number.isFinite(minute)
      ? (hour % 24) * 60 + minute
      : null;
  } catch {
    return null;
  }
}

/**
 * TASK-0249 (INC-1c): the next order a vendor's How we order asks for, from `asOf`: the next order day
 * (today counts until its cutoff has passed, when `nowMinutes` is given for today), and the first delivery
 * day it can make (its delivery days, else the days deliveries usually come, at least the lead time
 * later). Without order days it falls back to the delivery-based rule, with the vendor's lead time. Null
 * when nothing gives a day. The buyer's "no delivery expected until" (TASK-0239) holds here too.
 *
 * TASK-0256 (DROP-1): for a vendor with a drop-off location the delivery days are the days it DROPS there;
 * `dropOn` is that day and `deliveryOn` the day it reaches the store (`storeArrivalOn`). Days from past
 * deliveries are store days already (`dropOn` null). A direct vendor's days are exactly as before.
 */
export function nextOrder(
  asOf: string,
  profile: Partial<VendorProfile> | null | undefined,
  rhythm: Pick<VendorRhythm, 'usualWeekdays'> & { quietUntil?: string | null },
  opts: { nowMinutes?: number | null; leadDays?: number } = {}
): NextDelivery | null {
  return asNext(
    nextOrderChain(asOf, profile ?? null, deliveryWeekdays(rhythm), {
      nowMinutes: opts.nowMinutes ?? null,
      ...(opts.leadDays !== undefined ? { leadDays: opts.leadDays } : {}),
      quietUntil: rhythm.quietUntil ?? null,
      destination: destinationOf(profile),
    })
  );
}

/** The most common lead time on a vendor's lines, else the default. */
export function typicalLeadDays(lines: Pick<GuideLine, 'interval'>[]): number {
  const counts = new Map<number, number>();
  for (const line of lines) {
    // TASK-0256: the order-to-drop-off part only; the chain adds the transfer itself.
    const lead = line.interval.leadDays - (line.interval.transferDays ?? 0);
    counts.set(lead, (counts.get(lead) ?? 0) + 1);
  }
  let best = DEFAULT_LEAD_DAYS;
  let bestCount = 0;
  for (const [lead, count] of counts) {
    if (count > bestCount || (count === bestCount && lead < best)) {
      best = lead;
      bestCount = count;
    }
  }
  return best;
}

/** `Mon Thu`, or null when the vendor has no usual day. */
export function deliveryDaysLabel(
  rhythm: Pick<VendorRhythm, 'usualWeekdays'>
): string | null {
  const days = deliveryWeekdays(rhythm);
  return days.length > 0 ? days.map(weekdayName).join(' ') : null;
}

/* ── Answering a "deliveries look missing" flag (TASK-0239) ──────────────── */

/** "No delivery expected until" may reach this many days past today (the API enforces it too). */
export const MAX_QUIET_DAYS = 60;

export const OUTCOME_OPTIONS: ReadonlyArray<{ value: RhythmOutcome; label: string }> = [
  { value: 'not_entered_yet', label: 'Deliveries came, but are not entered yet' },
  {
    value: 'no_delivery_expected',
    label: 'No delivery expected (closed, holiday, paused)',
  },
  { value: 'stopped_buying', label: 'We stopped buying from them' },
  { value: 'other', label: 'Something else' },
];

/** The answer as a phrase inside a sentence: `no delivery expected until Oct 30`. */
export function outcomePhrase(
  r: Pick<RhythmResolution, 'outcome' | 'quietUntil' | 'note'>
): string {
  switch (r.outcome) {
    case 'not_entered_yet':
      return 'deliveries came, not entered yet';
    case 'no_delivery_expected':
      return r.quietUntil
        ? `no delivery expected until ${dayLabel(r.quietUntil)}`
        : 'no delivery expected';
    case 'stopped_buying':
      return 'stopped buying from them';
    default:
      return r.note?.trim() ? r.note.trim() : 'other';
  }
}

/**
 * `Acknowledged by Motti on Oct 14: deliveries came, not entered yet — invoices
 * are in the tray`. The note follows every outcome except "other", whose phrase
 * is the note itself.
 */
export function resolutionText(r: RhythmResolution): string {
  const note = r.outcome !== 'other' && r.note?.trim() ? ` — ${r.note.trim()}` : '';
  return `Acknowledged by ${r.byName ?? 'someone'} on ${dayLabel(r.on)}: ${outcomePhrase(r)}${note}`;
}

/**
 * The same answer short enough for a board cell, outcome first so a clipped line
 * loses the least: `Not entered yet · Motti, Oct 14`. The full sentence is
 * `resolutionText`, used as its tooltip and on the vendor guide.
 */
export function resolutionShort(r: RhythmResolution): string {
  const what =
    r.outcome === 'not_entered_yet'
      ? 'Not entered yet'
      : r.outcome === 'no_delivery_expected'
        ? `No delivery until ${dayLabel(r.quietUntil)}`
        : r.outcome === 'stopped_buying'
          ? 'Stopped buying'
          : (r.note?.trim() ?? 'Other');
  return `${what} · ${r.byName ?? 'someone'}, ${dayLabel(r.on)}`;
}

/** The answer to show against a vendor: only one that still answers the current silence. */
export function activeResolution(vendor: {
  rhythm: Pick<VendorRhythm, 'broken' | 'quietUntil'>;
  rhythmResolution: RhythmResolution | null;
}): RhythmResolution | null {
  const r = vendor.rhythmResolution;
  if (!r || !r.current) return null;
  // Nothing to show against a vendor that is neither flagged nor quiet.
  return vendor.rhythm.broken || vendor.rhythm.quietUntil ? r : null;
}

/** The order-to-usual ratio is worth a look outside 0.67x to 1.5x, both ways. */
export function ratioIsOff(ratio: number | null | undefined): boolean {
  return ratio != null && (ratio < 0.67 || ratio > 1.5);
}

export function ratioLabel(ratio: number | null | undefined): string {
  return ratio == null ? '—' : `${ratio.toFixed(1)}x`;
}

/* ── Quantities ─────────────────────────────────────────────────────────── */

/** `12`, `2.5`, `0.375`: at most 3 decimals, no trailing zeros. */
export function qtyText(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return String(Math.round(n * 1000) / 1000);
}

/** Units a week, one decimal: `13.2`. */
export function rateText(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? (Math.round(n * 10) / 10).toFixed(1) : '—';
}

/* ── Row wording ────────────────────────────────────────────────────────── */

const SUSPECT_REASON: Record<string, string> = {
  NEGATIVE_BALANCE: 'negative',
  STALE_COUNT: 'old count',
  INTAKE_GAP: 'deliveries missing',
};

export function stockText(
  stock: GuideLine['stock'],
  unit: GuideLine['unit']
): {
  text: string;
  title: string;
  tier: StockTier;
} {
  const u = unit === 'lb' ? ' lb' : '';
  switch (stock.tier) {
    case 'counted':
      return {
        text: stock.countedAt
          ? `${qtyText(stock.qty)}${u} counted ${localMonthDay.format(new Date(stock.countedAt))}`
          : `${qtyText(stock.qty)}${u} counted`,
        title: 'Counted stock, less what is held for orders',
        tier: 'counted',
      };
    case 'suspect': {
      const reason = SUSPECT_REASON[stock.reason ?? ''] ?? 'check';
      return {
        text: `suspect: ${reason}`,
        title:
          `On hand says ${qtyText(stock.systemOnHand)}${u}; ` +
          `using the estimate from deliveries (~${qtyText(stock.qty)}${u}) until it is counted again`,
        tier: 'suspect',
      };
    }
    case 'untracked':
      return {
        text: 'house-made',
        title: 'Not counted (house-made or supply item)',
        tier: 'untracked',
      };
    default:
      return {
        text: `~${qtyText(stock.qty)}${u} est`,
        title: 'Estimated: last delivery minus what sold since',
        tier: 'estimated',
      };
  }
}

/** The case-size chip: `12`, `12?` not confirmed, `12!` invoice disagrees, `ea`, `lb`, `~cs`. */
export function packChip(pack: GuideLine['pack']): {
  label: string;
  title: string;
  warn: boolean;
} {
  switch (pack.status) {
    case 'confirmed':
      return {
        label: String(pack.size),
        title:
          pack.confirmedBy === 'buyer'
            ? `${pack.size} a case (confirmed by the buyer)`
            : `${pack.size} a case`,
        warn: false,
      };
    case 'unverified':
      return {
        label: `${pack.size}?`,
        title: `${pack.size} a case, not confirmed by an invoice or past deliveries`,
        warn: true,
      };
    case 'conflict':
      return {
        label: `${pack.size}!`,
        title: `The catalog says ${pack.size} a case; invoices print ${pack.invoicePack}`,
        warn: true,
      };
    case 'invoice_only':
      return {
        label: String(pack.size),
        title: `${pack.size} a case (from invoices)`,
        warn: false,
      };
    case 'weighed':
      return pack.avgCaseWeightLbs
        ? {
            label: '~cs',
            title: `Sold by the pound; a case averages about ${qtyText(pack.avgCaseWeightLbs)} lb`,
            warn: false,
          }
        : { label: 'lb', title: 'Sold by the pound', warn: false };
    default:
      return { label: 'ea', title: 'Ordered by the piece', warn: false };
  }
}

/** Why a Check row's box was left empty, in the buyer's words. Null = say nothing. */
export function flagText(
  flag: string,
  line: GuideLine,
  vendorName?: string
): string | null {
  switch (flag) {
    case 'RECORDING_GAP':
      return line.lastIn && line.soldSinceLastIn
        ? `Deliveries may be missing: last recorded ${dayLabel(line.lastIn.on)}, ${qtyText(line.soldSinceLastIn)} sold since — check the shelf`
        : 'Deliveries from this vendor may be missing — check the shelf';
    case 'STALE_RECEIPT':
      return 'No delivery recorded in 16 weeks';
    case 'NO_RECEIPT':
      return 'Never recorded as received';
    case 'PACK_UNVERIFIED':
      return 'Case size not confirmed';
    case 'PACK_CONFLICT':
      return `Case size differs: catalog ${line.pack.size}, invoice ${line.pack.invoicePack}`;
    case 'CASE_PRICED_RECEIPT':
      return 'Last receipt looks entered at the case price';
    case 'HOLIDAY_DISTORTED':
      return 'Last two weeks were unusual (holiday?)';
    case 'OVERDUE_PO': {
      const late = line.onOrder.pos.filter((po) => po.overdue).map((po) => `#${po.poId}`);
      return late.length > 0
        ? `Late order ${late.join(', ')} is not counted as on order`
        : 'A late order is not counted as on order';
    }
    case 'LOW_HISTORY':
      return 'Less than 3 weeks of sales';
    case 'UNTRACKED':
      return 'House-made or supply item: not counted';
    case 'ONE_DELIVERY':
      return vendorName
        ? `Delivered once by ${vendorName} in 16 weeks`
        : 'Delivered once in 16 weeks';
    case 'LOW':
      return 'Low on counted stock';
    case 'MULTI_VENDOR_NO_PREFERRED':
      return 'Several vendors, none preferred';
    case 'PACK_FROM_INVOICE':
      return 'Case size from invoices';
    case 'PERISHABLE_COVER':
      return line.cover
        ? `Lasts about ${qtyText(line.cover.days)} days; shelf life about ${line.cover.maxDays}`
        : 'Lasts longer than its shelf life';
    case 'NO_SUPPLIER':
      return 'No usual vendor: link one, or mark one preferred';
    default:
      return null;
  }
}

/** Flags that leave the box empty (the API's blocking set). */
export const BLOCKING_FLAGS: ReadonlySet<string> = new Set([
  'RECORDING_GAP',
  'STALE_RECEIPT',
  'NO_RECEIPT',
  'PACK_UNVERIFIED',
  'PACK_CONFLICT',
  'CASE_PRICED_RECEIPT',
  'HOLIDAY_DISTORTED',
  'OVERDUE_PO',
  'LOW_HISTORY',
  'UNTRACKED',
]);

/** The reasons to show under a row: blocking first, then the informational ones worth a word. */
export function rowNotes(line: GuideLine, vendorName?: string): string[] {
  const blocking = line.flags.filter((f) => BLOCKING_FLAGS.has(f));
  const info = line.flags.filter(
    (f) =>
      !BLOCKING_FLAGS.has(f) &&
      (f === 'ONE_DELIVERY' || f === 'LOW' || f === 'PERISHABLE_COVER')
  );
  return [...blocking, ...info]
    .map((flag) => flagText(flag, line, vendorName))
    .filter((text): text is string => text !== null);
}

/** Why a week is skipped: a slow (closed) week or a busy (pre-holiday) week. */
export function weekReasonText(reason: StoreWeek['reason']): string | null {
  if (reason === 'low') return 'slow week (closed days)';
  if (reason === 'high') return 'busy week (before a holiday)';
  return null;
}
